import { useAdminTriage } from "@/hooks/queries/admin/useAdminTriage";

export default function AdminTriage(): JSX.Element {
  const { data, isLoading, error } = useAdminTriage();

  if (isLoading)
    return (
      <div className="text-sm text-text-secondary dark:text-brand-100/70">
        Loading…
      </div>
    );
  if (error) {
    return (
      <div className="text-sm text-red-600 dark:text-red-400">
        Couldn't load triage: {error.message}
      </div>
    );
  }
  if (!data)
    return (
      <div className="text-sm text-text-secondary dark:text-brand-100/70">
        No data.
      </div>
    );

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold text-text-primary dark:text-text-primary">
        Triage
      </h1>

      <Section
        title="Stuck impersonation sessions"
        empty="No stuck sessions."
        count={data.counts.impersonation_conflicts}
      >
        {data.impersonation_conflicts.map((row) => (
          <div
            key={row.impersonation_log_id}
            className={TRIAGE_CARD}
          >
            <div className="font-medium text-text-primary dark:text-text-primary">
              {row.super_admin_email ?? row.super_admin_user_id} →{" "}
              {row.target_tenant_slug ?? row.target_tenant_id ?? "?"}
            </div>
            <div className="text-xs text-accent-primary dark:text-brand-100/50">
              Started {new Date(row.started_at).toLocaleString()} · Reason:{" "}
              {row.reason ?? "—"}
            </div>
          </div>
        ))}
      </Section>

      <Section
        title="Failed subscriptions"
        empty="No failed subscriptions."
        count={data.counts.failed_subscriptions}
      >
        {data.failed_subscriptions.map((row) => (
          <div
            key={row.subscription_id}
            className={TRIAGE_CARD}
          >
            <div className="font-medium text-text-primary dark:text-text-primary">
              {row.tenant_slug}
            </div>
            <div className="text-xs text-accent-primary dark:text-brand-100/50">
              Status: {row.status} · past_due_since:{" "}
              {row.past_due_since
                ? new Date(row.past_due_since).toLocaleString()
                : "—"}
            </div>
          </div>
        ))}
      </Section>

      <Section
        title="Zombie tenants"
        empty="No zombie tenants."
        count={data.counts.zombie_tenants}
      >
        {data.zombie_tenants.map((row) => (
          <div
            key={row.tenant_id}
            className={TRIAGE_CARD}
          >
            <div className="font-medium text-text-primary dark:text-text-primary">
              {row.name} ({row.slug})
            </div>
            <div className="text-xs text-accent-primary dark:text-brand-100/50">
              Pending deletion since{" "}
              {row.pending_deletion_at
                ? new Date(row.pending_deletion_at).toLocaleString()
                : "—"}
              {row.days_until_sweep !== null
                ? ` · ${row.days_until_sweep}d until sweep`
                : ""}
            </div>
          </div>
        ))}
      </Section>

      <Section
        title="Failed Greenhouse syncs"
        empty="No failed syncs in the last 30 days."
        count={data.counts.failed_greenhouse_syncs}
      >
        {data.failed_greenhouse_syncs.map((row) => (
          <div
            key={row.sync_log_id}
            className={TRIAGE_CARD}
          >
            <div className="font-medium text-text-primary dark:text-text-primary">
              {row.tenant_slug} · {row.direction} · {row.entity_type}
            </div>
            <div className="text-xs text-accent-primary dark:text-brand-100/50">
              Started {new Date(row.started_at).toLocaleString()}
            </div>
            {row.error_message_redacted ? (
              <div className="mt-1 rounded bg-surface-canvas px-2 py-1 text-xs text-text-secondary dark:bg-white/[0.04] dark:text-brand-100/70">
                {row.error_message_redacted}
                {row.has_raw_error_message ? " (redacted)" : ""}
              </div>
            ) : null}
          </div>
        ))}
      </Section>
    </div>
  );
}

const TRIAGE_CARD =
  "rounded border border-brand-100 bg-white p-3 text-sm dark:border-white/[0.06] dark:bg-surface-card";

function Section({
  title,
  count,
  empty,
  children,
}: {
  title: string;
  count: number;
  empty: string;
  children: React.ReactNode;
}): JSX.Element {
  const childArray = Array.isArray(children) ? children : [children];
  const isEmpty = count === 0;
  return (
    <section>
      <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-text-secondary dark:text-brand-100/70">
        {title} ({count})
      </h2>
      {isEmpty ? (
        <div className="rounded border border-dashed border-brand-200 bg-white p-3 text-sm text-accent-primary dark:border-white/[0.08] dark:bg-surface-card dark:text-brand-100/50">
          {empty}
        </div>
      ) : (
        <div className="space-y-2">{childArray}</div>
      )}
    </section>
  );
}
